import React, { FC } from 'react';
import { ColumnSizing } from '../tables-context';
import { MRT_ColumnDef } from 'material-react-table';
import { IContact } from '../../contacts/contacts-interfaces';
import { TableCell } from '../../../components/table/TableCell';
import { numberFormatter } from '../../../utils/utilities';
import { getTableDate } from '../disputes/disputes-data';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router';
import { PIIComponent } from '../../../components/orders/PIIComponent';

export enum ContactsTableAccessorKeys {
	email = 'email',
	firstName = 'firstName',
	last4 = 'last4',
	totalSpend = 'totalSpend',
	payments = 'payments',
	refunds = 'refunds',
	disputeLost = 'disputeLost',
	lastPayment = 'lastPayment',
}

export const defaultContactsColumnSizing: ColumnSizing = {
	[ContactsTableAccessorKeys.email]: 100,
	[ContactsTableAccessorKeys.firstName]: 100,
	[ContactsTableAccessorKeys.last4]: 50,
	[ContactsTableAccessorKeys.totalSpend]: 75,
	[ContactsTableAccessorKeys.payments]: 50,
	[ContactsTableAccessorKeys.refunds]: 75,
	[ContactsTableAccessorKeys.disputeLost]: 75,
	[ContactsTableAccessorKeys.lastPayment]: 50,
};

export const defaultContactsColumns: MRT_ColumnDef<IContact>[] = [
	{
		accessorKey: ContactsTableAccessorKeys.firstName,
		header: 'Name',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Name</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<ContactName
					firstName={renderedCellValue}
					lastName={row?.original?.lastName}
					id={row?.original?.id}
				/>
			</TableCell>
		)
	},
	{
		accessorKey: ContactsTableAccessorKeys.email,
		header: 'Email',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Email</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<div className="fs-7 fc-text-overflow">
					<PIIComponent
						value={renderedCellValue}
						isHidden={true}
						type='email'
					/>
				</div>
			</TableCell>
		)
	},
	{
		accessorKey: ContactsTableAccessorKeys.last4,
		header: 'Default payment method',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Default payment method</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<div className="fs-7 fc-text-overflow table-row-paddings">
					{renderedCellValue ? renderedCellValue : '-'}
				</div>
			</TableCell>
		)
	},
	{
		accessorKey: ContactsTableAccessorKeys.totalSpend,
		header: 'Total spend',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Total spend</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<div className="fs-7 fc-text-overflow">
					${renderedCellValue ? numberFormatter(renderedCellValue) : numberFormatter(0)}
				</div>
			</TableCell>
		)
	},
	{
		accessorKey: ContactsTableAccessorKeys.payments,
		header: 'Payments',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Payments</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<div className="fs-7 fc-text-overflow">
					{renderedCellValue ? renderedCellValue : '-'}
				</div>
			</TableCell>
		)
	},
	{
		accessorKey: ContactsTableAccessorKeys.refunds,
		header: 'Refunds',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Refunds</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<div className="fs-7 fc-text-overflow">
					${renderedCellValue ? numberFormatter(renderedCellValue) : numberFormatter(0)}
				</div>
			</TableCell>
		)
	},
	{
		accessorKey: ContactsTableAccessorKeys.disputeLost,
		header: 'Dispute losses',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Dispute losses</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<div className="fs-7 fc-text-overflow">
					${renderedCellValue ? numberFormatter(renderedCellValue) : numberFormatter(0)}
				</div>
			</TableCell>
		)
	},
	{
		accessorKey: ContactsTableAccessorKeys.lastPayment,
		header: 'Last payment',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Last payment</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => {
			const year = new Date(renderedCellValue).getFullYear();
			return (
				year === 1
					? <div className="fw-bolder">-</div>
					: (
						<TableCell
							additionalClass="main-font-family text-black"
							density={table?.options?.state?.density}
							isTextOverflow={false}
						>
							{getTableDate(renderedCellValue, true)}
						</TableCell>
					)
			);
		}
	},
];

interface Props {
	firstName: string;
	lastName: string;
	id: string;
}

const ContactName: FC<Props> = (props) => {
	const { firstName ,lastName, id } = props;
	const navigate = useNavigate();
	const { pathname } = useLocation();
	return (
		<div
			className="fs-7 fc-text-overflow fw-bolder cursor-pointer"
			onClick={(): void => navigate(`${pathname}/${id}`)}
		>
			{firstName} {lastName}
		</div>
	);
};
